import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Badge, EmptyState, ErrorState, Skeleton, cn } from '@vta/ui';
import type { Program } from '@vta/types';
import { apiClient } from '../../lib/api-client.ts';
import { formatProgramLabel, programBadgeVariant } from '../../lib/format.ts';

/**
 * The /api/teams/:n/events response is shaped by apps/api/src/routes/teams.ts:
 *   { events: [{ eventId, eventCode, eventName, program, season,
 *                startDate, endDate, divisionName, divisionSlug,
 *                skillsRank, awards }] }
 *
 * We define the row shape locally rather than reaching into @vta/types because
 * the API row is a join projection, not a `Team` or `Event` row.
 */
interface TeamEventRow {
  eventId: string;
  eventCode: string;
  eventName: string;
  program: Program;
  season: string;
  startDate: string;
  endDate: string;
  divisionName: string | null;
  divisionSlug: string | null;
  skillsRank: number | null;
  awards: string[];
}

interface TeamEventsResponse {
  events: TeamEventRow[];
}

export interface TeamEventsListProps {
  teamNumber: string;
  className?: string;
}

export function TeamEventsList({ teamNumber, className }: TeamEventsListProps) {
  const query = useQuery<TeamEventsResponse>({
    queryKey: ['team', teamNumber, 'events'],
    queryFn: ({ signal }) =>
      apiClient.get<TeamEventsResponse>(`/api/teams/${encodeURIComponent(teamNumber)}/events`, {
        signal,
      }),
  });

  if (query.isLoading) {
    return (
      <div className={cn('flex flex-col gap-2', className)}>
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-2/3" />
      </div>
    );
  }

  if (query.isError) {
    return (
      <ErrorState
        title="Could not load events"
        description="Try again in a moment."
        retry={() => query.refetch()}
        className={className}
      />
    );
  }

  const rows = query.data?.events ?? [];

  if (rows.length === 0) {
    return (
      <EmptyState
        title="No events on file"
        description="Event history will appear here once we sync registrations for this team."
        className={className}
      />
    );
  }

  return (
    <ul className={cn('divide-y divide-border-subtle border-y border-border-subtle', className)}>
      {rows.map((row) => (
        <li
          key={`${row.eventId}-${row.divisionSlug ?? 'main'}`}
          className="flex flex-col gap-2 py-3 sm:flex-row sm:items-center sm:justify-between"
        >
          <div className="flex min-w-0 flex-col gap-0.5">
            <Link
              to="/event/$eventCode"
              params={{ eventCode: row.eventCode }}
              className="truncate text-sm text-fg transition-colors duration-quick hover:text-accent"
            >
              {row.eventName}
            </Link>
            <span className="text-2xs uppercase tracking-[0.18em] text-fg-subtle">
              {formatRange(row.startDate, row.endDate)} · {row.season}
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant={programBadgeVariant(row.program)}>
              {formatProgramLabel(row.program, 'short')}
            </Badge>
            {row.divisionSlug ? (
              <Link
                to="/division/$divisionSlug"
                params={{ divisionSlug: row.divisionSlug }}
                className="text-xs text-fg-muted underline-offset-2 hover:text-fg hover:underline"
              >
                {row.divisionName ?? row.divisionSlug}
              </Link>
            ) : null}
            {row.skillsRank ? (
              <span className="text-2xs uppercase tracking-[0.18em] text-fg-subtle">
                Skills #{row.skillsRank}
              </span>
            ) : null}
            {row.awards.length > 0 ? (
              <Badge variant="success">{row.awards.length} award{row.awards.length === 1 ? '' : 's'}</Badge>
            ) : null}
          </div>
        </li>
      ))}
    </ul>
  );
}

function formatRange(start: string, end: string): string {
  const s = new Date(start);
  const e = new Date(end);
  if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) return '';
  const fmt = new Intl.DateTimeFormat('en', { month: 'short', day: 'numeric', year: 'numeric' });
  if (s.toDateString() === e.toDateString()) return fmt.format(s);
  return `${fmt.format(s)} – ${fmt.format(e)}`;
}
